import { ChartCard, type ChartCardState } from "../../ChartCard";
import { HealthStrip } from "../../HealthStrip";
import type { TenantHealthTimeline } from "../../../../hooks/useAdminStatus";
import { rangeToWindowHours, type InstanceRange } from "../../../../lib/instanceDetailRange";

type HealthPanelProps = {
  readonly range: InstanceRange;
  readonly status: "loading" | "ready" | "error";
  readonly timeline: TenantHealthTimeline | null;
  readonly error: string | null;
};

const HEALTH_BUCKETS = 48;

const cardState = (
  status: HealthPanelProps["status"],
  timeline: TenantHealthTimeline | null,
): ChartCardState => {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (timeline == null || timeline.buckets.every((b) => b.state == null)) return "empty";
  return "ready";
};

export const HealthPanel = ({ range, status, timeline, error }: HealthPanelProps): JSX.Element => {
  // The per-tenant endpoint doesn't echo bucketSeconds; derive it from the
  // window the same way the backend does (windowHours * 3600 / 48).
  const bucketSeconds = (rangeToWindowHours(range) * 3600) / HEALTH_BUCKETS;
  const uptime = timeline?.uptimePct;
  return (
    <ChartCard
      title="Health"
      right={uptime != null ? `${uptime.toFixed(1)}% up` : undefined}
      state={cardState(status, timeline)}
      emptyText="No probe data in this window."
      errorText={error ?? undefined}
    >
      {timeline != null && (
        <HealthStrip buckets={timeline.buckets} bucketSeconds={bucketSeconds} tall />
      )}
    </ChartCard>
  );
};
